/**
 * holidayPlanning — 10/02/2026 (Lot M).
 *
 * Retour user prod : « Ok pour suggestion [panneau de planification].
 * Avec un conseil de la part de la responsable RH qui peut suggérer
 * des plans de planification et prévenir l'apprenant par un message
 * au préalable. »
 *
 * 2 rôles principaux :
 *   1. `buildHolidayPlan(drivers, currentDay)` → calcule un PLAN étalé
 *      des prochaines prises de congés, par ordre de priorité (pression
 *      sociale + solde élevé + ancienneté de la dernière prise). Décale
 *      automatiquement les départs de 7 jours pour éviter de vider
 *      l'effectif d'un coup.
 *   2. `buildNadiaPlanProposalMessage(plan)` → format inbox message
 *      proposé par la responsable RH (Nadia Cheikh) pour annoncer le
 *      plan au joueur.
 *
 * Critères pour intégrer le plan :
 *   • holidayDaysRemaining >= 5 (le minimum pour partir)
 *   • pas déjà en congés
 *   • pas en formation / arrêt / grève
 *   • newcomerLock OK (hiredAt + 60 j)
 *
 * Tri descendant par score = (pressureLoss × 3) + (balance × 0.4) +
 *                            ((currentDay - lastHolidayStart) × 0.1)
 *
 * Plan limité à 5 propositions max (sinon écran surchargé). Premier
 * départ : J+0 (dès cette semaine), puis J+7, J+14, J+21, J+28.
 */

const PLAN_INTERVAL_DAYS = 7;
const PLAN_MAX_SIZE = 5;
const PLAN_MIN_BALANCE = 5;
const NEWCOMER_LOCK_DAYS = 60;

/**
 * Score à minimiser : plus haut = plus urgent à envoyer en congés.
 */
function planScore(driver, currentDay) {
  const pressureLoss = driver.holidayPressureLossThisYear || 0;
  const balance = driver.holidayDaysRemaining || 0;
  const lastStart = driver.holidayStartDay || -180;
  const daysSinceLast = Math.max(0, currentDay - lastStart);
  return pressureLoss * 3 + balance * 0.4 + daysSinceLast * 0.1;
}

/**
 * Returns the eligibility of a driver for the planning panel.
 * @returns {{ eligible: boolean, reason?: string }}
 */
export function getDriverPlanEligibility(driver, currentDay) {
  if (!driver) return { eligible: false, reason: 'not_found' };
  if (driver.onHoliday) return { eligible: false, reason: 'already_on_holiday' };
  if (driver.status === 'sick') return { eligible: false, reason: 'sick' };
  if (driver.status === 'training') return { eligible: false, reason: 'training' };
  if (driver.status === 'striking') return { eligible: false, reason: 'striking' };
  if ((driver.holidayDaysRemaining || 0) < PLAN_MIN_BALANCE) return { eligible: false, reason: 'insufficient_balance' };
  const hiredAt = driver.hiredAt || 0;
  if (currentDay - hiredAt < NEWCOMER_LOCK_DAYS) {
    return { eligible: false, reason: 'newcomer_lock' };
  }
  return { eligible: true };
}

/**
 * Builds an ordered plan of upcoming holiday departures.
 *
 * @param {Array} drivers   - all drivers (will be filtered)
 * @param {number} currentDay - state.totalDays
 * @returns {Array<{
 *   driverId: string,
 *   driverName: string,
 *   plannedStartDay: number,
 *   plannedReturnDay: number,
 *   balance: number,
 *   pressureLoss: number,
 *   riskTone: 'low' | 'medium' | 'high',
 * }>}
 */
export function buildHolidayPlan(drivers, currentDay = 0) {
  if (!Array.isArray(drivers)) return [];
  const eligible = drivers
    .map(d => ({ driver: d, elig: getDriverPlanEligibility(d, currentDay) }))
    .filter(({ elig }) => elig.eligible);
  // Sort by descending plan score
  eligible.sort((a, b) => planScore(b.driver, currentDay) - planScore(a.driver, currentDay));
  const top = eligible.slice(0, PLAN_MAX_SIZE);
  return top.map(({ driver }, idx) => {
    const plannedStartDay = currentDay + idx * PLAN_INTERVAL_DAYS;
    const balance = driver.holidayDaysRemaining || 0;
    const pressureLoss = driver.holidayPressureLossThisYear || 0;
    // Risk tone:
    //   high   : pressure loss >= 5 (Nadia mid-cap) OR balance >= 25 (cap)
    //   medium : balance >= 18 OR pressure loss > 0
    //   low    : sinon
    let riskTone = 'low';
    if (pressureLoss >= 5 || balance >= 25) riskTone = 'high';
    else if (balance >= 18 || pressureLoss > 0) riskTone = 'medium';
    return {
      driverId: driver.id,
      driverName: driver.name,
      plannedStartDay,
      plannedReturnDay: plannedStartDay + 5,
      balance,
      pressureLoss,
      riskTone,
    };
  });
}

/**
 * Whether Nadia should push a planning proposal this tick.
 *   • Nadia must be hired (`role-nadia-cheikh`)
 *   • At least 1 driver in `high` risk
 *   • Pas déjà notifié cette année (state.holidayPlanProposedYear)
 *   • Mois >= 5 (mai) — début de l'urgence congés française
 */
export function shouldNadiaProposeHolidayPlan(state) {
  if (!state) return false;
  const hired = !!(state.hiredSupport || {})['role-nadia-cheikh'];
  if (!hired) return false;
  const month = state.month || 0;
  if (month < 5) return false;
  // Year tracking : push once per game year.
  const year = state.year || 1;
  if (state.holidayPlanProposedYear === year) return false;
  // Build plan to see if there's any high-risk driver.
  const plan = buildHolidayPlan(state.drivers || [], state.totalDays || 0);
  return plan.some(p => p.riskTone === 'high');
}

/**
 * Builds the inbox message payload that Nadia sends with the plan.
 */
export function buildNadiaPlanProposalMessage(plan, year) {
  const highRisk = plan.filter(p => p.riskTone === 'high');
  const lines = plan.map((p, i) => {
    const tag = p.riskTone === 'high' ? '🔴' : p.riskTone === 'medium' ? '🟠' : '🟢';
    const when = i === 0 ? 'cette semaine' : `dans ${i * 7} jours environ (J+${p.plannedStartDay})`;
    return `${tag} **${p.driverName}** — ${when} (solde ${p.balance} j${p.pressureLoss > 0 ? `, ${p.pressureLoss} pts satisfaction déjà perdus` : ''})`;
  });
  return {
    senderType: 'support',
    senderName: 'Nadia Cheikh (Resp. RH)',
    senderRole: 'role-nadia-cheikh',
    tone: 'advice',
    kind: 'warning',
    subject: `📅 Plan de congés ${year} — ${highRisk.length} chauffeur(s) prioritaires`,
    body:
      `Patron,\n\n` +
      `Voici mon **plan de prise de congés étalé** pour ${year}, classé par ordre de priorité :\n\n` +
      `${lines.join('\n\n')}\n\n` +
      `**Pourquoi cet ordre ?**\n` +
      `Les chauffeurs en 🔴 ont déjà perdu de la satisfaction sur la pression congés ou ont accumulé trop de solde (risque de départ négocié au 31 décembre).\n` +
      `J'ai étalé les départs de 7 jours pour ne pas vider l'effectif d'un coup. L'intérim sera activé automatiquement sur les véhicules en mission pendant les absences.\n\n` +
      `**Action à mener** : rendez-vous sur la page **RH → Planification congés** pour confirmer les départs un par un.\n\n` +
      `— Nadia Cheikh\nResponsable RH`,
    ttlDays: 30,
    payload: { kind: 'holiday_plan_proposal', planSize: plan.length },
  };
}

/**
 * Pretty-prints a risk tone with French labels for UI.
 */
export const RISK_TONE_LABELS = {
  high:   { label: 'Urgent',   color: 'rose' },
  medium: { label: 'À prévoir', color: 'amber' },
  low:    { label: 'Confortable', color: 'emerald' },
};

export const PLAN_CONSTANTS = {
  INTERVAL_DAYS: PLAN_INTERVAL_DAYS,
  MAX_SIZE: PLAN_MAX_SIZE,
  MIN_BALANCE: PLAN_MIN_BALANCE,
  NEWCOMER_LOCK_DAYS,
};
